import { Classes } from '@clarion-blueprint-shim';
import { ReactNode, useLayoutEffect, useRef } from 'react';
import {
  TextArea as RACTextArea,
  TextAreaProps as RACTextAreaProps,
  TextField as RACTextField,
  TextFieldProps as RACTextFieldProps,
  composeRenderProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { cx } from '../util';
import { ClarionDescription, ClarionFieldError, ClarionLabel } from './ClarionField';

export interface ClarionTextAreaProps extends Omit<RACTextFieldProps, 'children'> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: any) => string);
  placeholder?: string;
  rows?: number;
  cols?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  fill?: boolean;
  size?: 'small' | 'medium' | 'large';
  autoResize?: boolean;
  children?: ReactNode;
  textAreaProps?: RACTextAreaProps;
}

const textAreaStyles = tv({
  base: cx(Classes.INPUT, Classes.TEXT_AREA),
  variants: {
    isFocusWithin: {
      false: 'border-gray-300 dark:border-gray-500',
      true: 'border-gray-600 dark:border-gray-300',
    },
    isInvalid: {
      true: Classes.INTENT_DANGER,
    },
    isDisabled: {
      true: Classes.DISABLED,
    },
    size: {
      small: Classes.SMALL,
      medium: '',
      large: Classes.LARGE,
    },
    resize: {
      none: 'resize-none',
      both: 'resize',
      horizontal: 'resize-x',
      vertical: 'resize-y',
    },
    fill: {
      true: Classes.FILL,
    },
  },
  defaultVariants: {
    size: 'medium',
    resize: 'vertical',
  },
});

const containerStyles = tv({
  base: 'flex flex-col gap-1',
  variants: {
    fill: {
      true: 'w-full',
    },
  },
});

export function ClarionTextArea({
  label,
  description,
  errorMessage,
  placeholder,
  rows = 1,
  cols,
  resize = 'vertical',
  fill,
  size = 'medium',
  autoResize = false,
  children,
  className,
  textAreaProps,
  ...props
}: ClarionTextAreaProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const textArea = textAreaRef.current;
    if (!autoResize || !textArea) return;

    const adjustHeight = () => {
      // Reset height to auto to get the correct scrollHeight
      textArea.style.height = 'auto';
      // Set height to scrollHeight to fit content exactly
      textArea.style.height = `${textArea.scrollHeight}px`;

      // If there's still scrolling, add a small buffer
      if (textArea.scrollHeight > textArea.clientHeight) {
        textArea.style.height = `${textArea.scrollHeight + 2}px`;
      }
    };

    adjustHeight();
    textArea.addEventListener('input', adjustHeight);
    return () => {
      textArea.removeEventListener('input', adjustHeight);
    };
  }, [autoResize]);

  return (
    <RACTextField
      {...props}
      className={composeRenderProps(className, (className) => containerStyles({ fill, className }))}
    >
      {label && <ClarionLabel>{label}</ClarionLabel>}
      <RACTextArea
        {...textAreaProps}
        ref={textAreaRef}
        placeholder={placeholder}
        rows={rows}
        cols={cols}
        className={composeRenderProps(textAreaProps?.className, (_, renderProps) =>
          textAreaStyles({
            ...renderProps,
            size,
            resize: autoResize ? 'none' : resize,
            fill,
          }),
        )}
      />
      {description && <ClarionDescription>{description}</ClarionDescription>}
      <ClarionFieldError>{errorMessage}</ClarionFieldError>
      {children}
    </RACTextField>
  );
}
